import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { suporteAPI } from '@/lib/api';
import {
  Headset, Plus, Send, ArrowLeft, Clock, CheckCircle2, AlertCircle,
  Loader2, MessageSquare, Settings, Trash2, Edit2, X, Save,
  CircleDot, Timer, CheckCheck, Lock
} from 'lucide-react';

const STATUS_CONFIG = {
  ABERTO: { label: 'Aberto', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: AlertCircle, dotColor: 'bg-yellow-500' },
  EM_ATENDIMENTO: { label: 'Em Atendimento', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Timer, dotColor: 'bg-blue-500' },
  RESOLVIDO: { label: 'Resolvido', color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2, dotColor: 'bg-green-500' },
  ENCERRADO: { label: 'Encerrado', color: 'bg-gray-100 text-gray-600 border-gray-300', icon: Lock, dotColor: 'bg-gray-500' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.ABERTO;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
};

// ========== GERENCIAR TIPOS (ADMIN) ==========
const GerenciarTipos = ({ onClose }) => {
  const [tipos, setTipos] = useState([]);
  const [novoTipo, setNovoTipo] = useState('');
  const [editando, setEditando] = useState(null);
  const [editNome, setEditNome] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { carregarTipos(); }, []);

  const carregarTipos = async () => {
    try {
      const res = await suporteAPI.listarTipos();
      setTipos(res.data);
    } catch (e) { console.error(e); }
  };

  const criarTipo = async () => {
    if (!novoTipo.trim()) return;
    setLoading(true);
    try {
      await suporteAPI.criarTipo({ nome: novoTipo.trim() });
      setNovoTipo('');
      carregarTipos();
    } catch (e) { alert(e.response?.data?.detail || 'Erro ao criar tipo'); }
    finally { setLoading(false); }
  };

  const salvarEdicao = async (id) => {
    if (!editNome.trim()) return;
    try {
      await suporteAPI.atualizarTipo(id, { nome: editNome.trim() });
      setEditando(null);
      carregarTipos();
    } catch (e) { alert('Erro ao atualizar'); }
  };

  const excluirTipo = async (id) => {
    if (!window.confirm('Excluir este tipo?')) return;
    try {
      await suporteAPI.excluirTipo(id);
      carregarTipos();
    } catch (e) { alert('Erro ao excluir'); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-white">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-orange-600" />
            <h2 className="text-lg font-bold">Gerenciar Tipos de Ticket</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Nome do tipo (ex: Problema com sistema)"
              value={novoTipo}
              onChange={(e) => setNovoTipo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && criarTipo()}
              data-testid="new-tipo-input"
            />
            <Button onClick={criarTipo} disabled={loading || !novoTipo.trim()} className="bg-orange-600 hover:bg-orange-700" data-testid="add-tipo-btn">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {tipos.map((tipo) => (
              <div key={tipo.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                {editando === tipo.id ? (
                  <div className="flex-1 flex gap-2">
                    <Input value={editNome} onChange={(e) => setEditNome(e.target.value)} className="h-8 text-sm" />
                    <Button size="sm" onClick={() => salvarEdicao(tipo.id)} className="h-8 bg-green-600 hover:bg-green-700"><Save className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditando(null)} className="h-8"><X className="h-3 w-3" /></Button>
                  </div>
                ) : (
                  <>
                    <span className="text-sm font-medium">{tipo.nome}</span>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditando(tipo.id); setEditNome(tipo.nome); }} className="text-gray-400 hover:text-blue-600 p-1"><Edit2 className="h-3.5 w-3.5" /></button>
                      <button onClick={() => excluirTipo(tipo.id)} className="text-gray-400 hover:text-red-600 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </>
                )}
              </div>
            ))}
            {tipos.length === 0 && <p className="text-center text-gray-400 text-sm py-4">Nenhum tipo cadastrado</p>}
          </div>
        </div>
      </Card>
    </div>
  );
};

// ========== CRIAR TICKET ==========
const CriarTicket = ({ onClose, onCriado }) => {
  const [tipos, setTipos] = useState([]);
  const [tipo, setTipo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    suporteAPI.listarTipos().then(res => setTipos(res.data)).catch(() => {});
  }, []);

  const enviar = async () => {
    if (!tipo || !descricao.trim()) return;
    setLoading(true);
    try {
      const res = await suporteAPI.criarTicket({ tipo, descricao: descricao.trim() });
      onCriado(res.data);
      onClose();
    } catch (e) { alert(e.response?.data?.detail || 'Erro ao criar ticket'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-white">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-orange-600" />
            <h2 className="text-lg font-bold">Abrir Ticket de Suporte</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Solicitação</label>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid="ticket-tipo-select"
            >
              <option value="">Selecione o tipo...</option>
              {tipos.map((t) => (
                <option key={t.id} value={t.nome}>{t.nome}</option>
              ))}
            </select>
            {tipos.length === 0 && <p className="text-xs text-red-500 mt-1">Nenhum tipo cadastrado. Peça ao ADMIN para configurar.</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição da Solicitação</label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={4}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
              placeholder="Descreva detalhadamente sua solicitação..."
              data-testid="ticket-descricao-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={enviar} disabled={loading || !tipo || !descricao.trim()} className="bg-orange-600 hover:bg-orange-700" data-testid="ticket-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Enviar Ticket
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ========== DETALHES DO TICKET ==========
const DetalhesTicket = ({ ticketId, onVoltar, isSuporteUser }) => {
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [novoStatus, setNovoStatus] = useState('');
  const [prazo, setPrazo] = useState('');
  const [solucao, setSolucao] = useState('');
  const messagesEndRef = useRef(null);
  const refreshRef = useRef(null);

  const carregarTicket = useCallback(async () => {
    try {
      const [ticketRes, msgsRes] = await Promise.all([
        suporteAPI.obterTicket(ticketId),
        suporteAPI.listarMensagens(ticketId),
      ]);
      setTicket(ticketRes.data);
      setMensagens(msgsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => {
    carregarTicket();
    refreshRef.current = setInterval(() => { if (!document.hidden) carregarTicket(); }, 12000);
    return () => clearInterval(refreshRef.current);
  }, [carregarTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  const enviarMsg = async () => {
    if (!novaMensagem.trim() || enviando) return;
    setEnviando(true);
    try {
      await suporteAPI.enviarMensagem(ticketId, { texto: novaMensagem.trim() });
      setNovaMensagem('');
      carregarTicket();
    } catch (e) { alert('Erro ao enviar mensagem'); }
    finally { setEnviando(false); }
  };

  const atualizarStatus = async () => {
    if (!novoStatus) return;
    try {
      await suporteAPI.atualizarStatus(ticketId, {
        status: novoStatus,
        prazo: prazo || null,
        solucao: solucao || null,
      });
      setShowStatusModal(false);
      setNovoStatus('');
      setPrazo('');
      setSolucao('');
      carregarTicket();
    } catch (e) { alert(e.response?.data?.detail || 'Erro ao atualizar status'); }
  };

  const formatarHora = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
    </div>
  );

  if (!ticket) return <p className="text-center text-gray-500 py-8">Ticket não encontrado</p>;

  const isClosed = ticket.status === 'ENCERRADO';

  return (
    <div className="h-full flex flex-col" data-testid="ticket-detail">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onVoltar} data-testid="ticket-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900">{ticket.protocolo}</h2>
              <StatusBadge status={ticket.status} />
            </div>
            <p className="text-sm text-gray-500">{ticket.tipo} — por {ticket.criado_por_nome}</p>
          </div>
        </div>
        {isSuporteUser && !isClosed && (
          <Button onClick={() => setShowStatusModal(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="change-status-btn">
            Alterar Status
          </Button>
        )}
      </div>

      {/* Info do Ticket */}
      <Card className="mb-4 p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><span className="text-gray-500">Criado em:</span><br/><span className="font-medium">{formatarHora(ticket.criado_em)}</span></div>
          <div><span className="text-gray-500">Atendido por:</span><br/><span className="font-medium">{ticket.atendido_por_nome || '—'}</span></div>
          <div><span className="text-gray-500">Prazo:</span><br/><span className="font-medium">{ticket.prazo || '—'}</span></div>
          <div><span className="text-gray-500">Solução:</span><br/><span className="font-medium">{ticket.solucao || '—'}</span></div>
        </div>
        <div className="mt-3 pt-3 border-t">
          <p className="text-sm text-gray-500 mb-1">Descrição:</p>
          <p className="text-sm text-gray-800">{ticket.descricao}</p>
        </div>
      </Card>

      {/* Mensagens */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 py-2 bg-gray-50 border-b text-sm font-semibold text-gray-600 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" /> Conversação
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {mensagens.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-8">Nenhuma mensagem ainda. Inicie a conversa abaixo.</p>
          )}
          {mensagens.map((msg) => {
            const isMe = msg.autor_id === user?.id;
            const isSuporte = msg.autor_perfil === 'ADMIN' || msg.autor_perfil === 'BKO';
            return (
              <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl shadow-sm ${
                  isMe
                    ? 'bg-orange-600 text-white rounded-br-md'
                    : 'bg-white text-gray-800 rounded-bl-md border'
                }`}>
                  {!isMe && (
                    <p className={`text-xs font-semibold mb-1 ${isSuporte ? 'text-orange-600' : 'text-blue-600'}`}>
                      {msg.autor_nome} {isSuporte ? '(Suporte)' : ''}
                    </p>
                  )}
                  <p className="whitespace-pre-wrap break-words text-sm">{msg.texto}</p>
                  <p className={`text-xs mt-1 text-right ${isMe ? 'text-orange-200' : 'text-gray-400'}`}>
                    {formatarHora(msg.criado_em)}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        {!isClosed ? (
          <div className="bg-white border-t px-4 py-3 flex items-center gap-3">
            <Input
              placeholder="Digite sua mensagem..."
              value={novaMensagem}
              onChange={(e) => setNovaMensagem(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && enviarMsg()}
              disabled={enviando}
              data-testid="ticket-msg-input"
            />
            <Button onClick={enviarMsg} disabled={!novaMensagem.trim() || enviando} className="bg-orange-600 hover:bg-orange-700" data-testid="ticket-msg-send">
              {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </Button>
          </div>
        ) : (
          <div className="bg-gray-100 px-4 py-3 text-center text-sm text-gray-500">
            <Lock className="h-4 w-4 inline mr-1" /> Ticket encerrado — não é possível enviar mensagens
          </div>
        )}
      </Card>

      {/* Modal Alterar Status */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-white">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="font-bold">Alterar Status do Ticket</h3>
              <button onClick={() => setShowStatusModal(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Novo Status</label>
                <select
                  value={novoStatus}
                  onChange={(e) => setNovoStatus(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="status-select"
                >
                  <option value="">Selecione...</option>
                  {ticket.status === 'ABERTO' && <option value="EM_ATENDIMENTO">Em Atendimento</option>}
                  {(ticket.status === 'ABERTO' || ticket.status === 'EM_ATENDIMENTO') && <option value="RESOLVIDO">Resolvido</option>}
                  {ticket.status === 'RESOLVIDO' && <option value="ENCERRADO">Encerrado</option>}
                  {ticket.status === 'EM_ATENDIMENTO' && <option value="ENCERRADO">Encerrado</option>}
                </select>
              </div>
              {(novoStatus === 'EM_ATENDIMENTO') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prazo estimado</label>
                  <Input value={prazo} onChange={(e) => setPrazo(e.target.value)} placeholder="Ex: 2 dias úteis" data-testid="prazo-input" />
                </div>
              )}
              {(novoStatus === 'RESOLVIDO') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Solução aplicada</label>
                  <textarea
                    value={solucao}
                    onChange={(e) => setSolucao(e.target.value)}
                    rows={3}
                    className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                    placeholder="Descreva a solução..."
                    data-testid="solucao-input"
                  />
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowStatusModal(false)}>Cancelar</Button>
                <Button onClick={atualizarStatus} disabled={!novoStatus} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-status-btn">
                  Confirmar
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ========== PÁGINA PRINCIPAL SUPORTE ==========
const Suporte = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [showCriarTicket, setShowCriarTicket] = useState(false);
  const [showGerenciarTipos, setShowGerenciarTipos] = useState(false);
  const [ticketSelecionado, setTicketSelecionado] = useState(null);
  const [stats, setStats] = useState(null);

  const isSuporteUser = user?.perfil === 'ADMIN' || user?.perfil === 'BKO';

  const carregarTickets = useCallback(async () => {
    try {
      const params = {};
      if (filtroStatus) params.status = filtroStatus;
      const res = await suporteAPI.listarTickets(params);
      setTickets(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filtroStatus]);

  useEffect(() => {
    carregarTickets();
    if (isSuporteUser) {
      suporteAPI.estatisticas().then(res => setStats(res.data)).catch(() => {});
    }
  }, [carregarTickets, isSuporteUser]);

  const formatarData = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (ticketSelecionado) {
    return (
      <div className="h-[calc(100vh-120px)]">
        <DetalhesTicket
          ticketId={ticketSelecionado}
          onVoltar={() => { setTicketSelecionado(null); carregarTickets(); }}
          isSuporteUser={isSuporteUser}
        />
      </div>
    );
  }

  return (
    <div data-testid="suporte-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
            <Headset className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Suporte</h1>
            <p className="text-gray-500 text-sm">Central de atendimento e tickets</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user?.perfil === 'ADMIN' && (
            <Button variant="outline" onClick={() => setShowGerenciarTipos(true)} data-testid="manage-tipos-btn">
              <Settings className="h-4 w-4 mr-2" /> Tipos
            </Button>
          )}
          <Button onClick={() => setShowCriarTicket(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="open-ticket-btn">
            <Plus className="h-4 w-4 mr-2" /> Abrir Ticket
          </Button>
        </div>
      </div>

      {/* Estatísticas (ADMIN/BKO) */}
      {isSuporteUser && stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total', value: stats.total, color: 'text-gray-800', bg: 'bg-gray-50' },
            { label: 'Abertos', value: stats.abertos, color: 'text-yellow-700', bg: 'bg-yellow-50' },
            { label: 'Em Atendimento', value: stats.em_atendimento, color: 'text-blue-700', bg: 'bg-blue-50' },
            { label: 'Resolvidos', value: stats.resolvidos, color: 'text-green-700', bg: 'bg-green-50' },
            { label: 'Encerrados', value: stats.encerrados, color: 'text-gray-600', bg: 'bg-gray-50' },
          ].map((s, i) => (
            <Card key={i} className={`${s.bg} p-3 text-center`}>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </Card>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-gray-500">Filtrar:</span>
        {['', 'ABERTO', 'EM_ATENDIMENTO', 'RESOLVIDO', 'ENCERRADO'].map((s) => (
          <button
            key={s}
            onClick={() => { setFiltroStatus(s); setLoading(true); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filtroStatus === s
                ? 'bg-orange-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`filter-${s || 'todos'}`}
          >
            {s ? STATUS_CONFIG[s]?.label : 'Todos'}
          </button>
        ))}
      </div>

      {/* Lista de Tickets */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Headset className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">Nenhum ticket encontrado</p>
            <p className="text-sm mt-1">Clique em "Abrir Ticket" para criar um novo</p>
          </div>
        ) : (
          <div className="divide-y">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => setTicketSelecionado(ticket.id)}
                className="flex items-center justify-between px-5 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                data-testid={`ticket-row-${ticket.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-orange-700">{ticket.protocolo}</span>
                    <StatusBadge status={ticket.status} />
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{ticket.tipo}</span>
                  </div>
                  <p className="text-sm text-gray-700 truncate">{ticket.descricao}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Por: {ticket.criado_por_nome} • {formatarData(ticket.criado_em)}
                    {ticket.atendido_por_nome && ` • Atendido por: ${ticket.atendido_por_nome}`}
                  </p>
                </div>
                <ArrowLeft className="h-4 w-4 text-gray-300 rotate-180 flex-shrink-0 ml-3" />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Modais */}
      {showCriarTicket && (
        <CriarTicket
          onClose={() => setShowCriarTicket(false)}
          onCriado={() => carregarTickets()}
        />
      )}
      {showGerenciarTipos && (
        <GerenciarTipos onClose={() => setShowGerenciarTipos(false)} />
      )}
    </div>
  );
};

export default Suporte;
